import type { SimulationPlayback, SimulationPlaybackCluster, SimulationPlaybackFrame } from './simulationPlayback';

export type ResultCapability = 'AVAILABLE' | 'PARTIAL' | 'NOT_AVAILABLE';
export type ResultDataType = 'CONTINUOUS' | 'DIVERGING' | 'CATEGORICAL';
export type ResultGranularity = 'FRAME_SCALAR' | 'CLUSTER_SCALAR' | 'PROFILE' | 'SURFACE_FIELD' | 'SURFACE_CELL' | 'SURFACE_VERTEX' | 'BRANCH_SEGMENT';
export type ResultGroup = 'Geometry' | 'Pressure / Stress' | 'Proppant' | 'Post-Frac' | 'Fluid';
export type ResultRangeScope = 'GLOBAL' | 'CURRENT_FRAME';
export type ResultRangeMethod = 'MIN_MAX' | 'P2_P98' | 'MANUAL';

export type ResultPropertyId =
  | 'dynamic_width' | 'propped_width' | 'half_length' | 'height'
  | 'fracture_pressure' | 'net_pressure' | 'local_shmin' | 'stress_shadow'
  | 'proppant_concentration' | 'proppant_areal_concentration' | 'proppant_bed' | 'proppant_type'
  | 'conductivity' | 'fluid_type';

export type ResultRange = {
  min: number;
  max: number;
  available: boolean;
  constant: boolean;
  clipped?: boolean;
  center?: number;
};

export type ResultPropertyDefinition = {
  id: ResultPropertyId;
  label: string;
  unit: string;
  group: ResultGroup;
  capability: ResultCapability;
  capabilityReason?: string;
  dataType: ResultDataType;
  granularity: ResultGranularity;
  branchGranularity?: 'BRANCH_SEGMENT';
  colorMap: keyof typeof COLOR_MAP_REGISTRY;
  getValue?: (cluster: SimulationPlaybackCluster) => number | undefined;
  getCategory?: (cluster: SimulationPlaybackCluster) => string | undefined;
};

const finite = (value: number | undefined): number | undefined =>
  value !== undefined && Number.isFinite(value) ? value : undefined;
const nonNegative = (value: number | undefined): number | undefined => {
  const parsed = finite(value);
  return parsed === undefined || parsed < 0 ? undefined : parsed;
};

export const COLOR_MAP_REGISTRY = {
  viridis: ['#440154', '#3B528B', '#21918C', '#5EC962', '#FDE725'],
  pressure: ['#081D58', '#225EA8', '#41B6C4', '#FED976', '#F03B20'],
  plasma: ['#0D0887', '#7E03A8', '#CC4778', '#F89540', '#F0F921'],
  inferno: ['#000004', '#420A68', '#932667', '#DD513A', '#FCA50A', '#FCFFA4'],
  diverging: ['#2166AC', '#67A9CF', '#F7F7F7', '#EF8A62', '#B2182B'],
  categorical: ['#2874A6', '#D4770C', '#1E8449', '#7D3C98', '#C0392B', '#566573'],
} as const;

export const RESULT_PROPERTIES: Record<ResultPropertyId, ResultPropertyDefinition> = {
  dynamic_width: {
    id: 'dynamic_width', label: '缝宽', unit: 'mm', group: 'Geometry',
    capability: 'AVAILABLE', dataType: 'CONTINUOUS', granularity: 'SURFACE_VERTEX', colorMap: 'viridis',
    getValue: (cluster) => cluster.hasWidthResult ? nonNegative(cluster.actualWidthMm) : undefined,
  },
  propped_width: {
    id: 'propped_width', label: '压后缝宽', unit: 'mm', group: 'Geometry',
    capability: 'PARTIAL', capabilityReason: 'SURFACE_2D_V1 输出逐 surface-cell 压后缝宽；旧模型保持不可用。',
    dataType: 'CONTINUOUS', granularity: 'SURFACE_CELL', branchGranularity: 'BRANCH_SEGMENT', colorMap: 'viridis',
  },
  half_length: {
    id: 'half_length', label: '半缝长', unit: 'm', group: 'Geometry',
    capability: 'AVAILABLE', dataType: 'CONTINUOUS', granularity: 'FRAME_SCALAR', colorMap: 'viridis',
    getValue: (cluster) => nonNegative(cluster.lengthM),
  },
  height: {
    id: 'height', label: '缝高', unit: 'm', group: 'Geometry',
    capability: 'AVAILABLE', dataType: 'CONTINUOUS', granularity: 'FRAME_SCALAR', colorMap: 'viridis',
    getValue: (cluster) => nonNegative(cluster.heightM),
  },
  fracture_pressure: {
    id: 'fracture_pressure', label: '裂缝压力', unit: 'MPa', group: 'Pressure / Stress',
    capability: 'AVAILABLE', dataType: 'CONTINUOUS', granularity: 'SURFACE_VERTEX', colorMap: 'pressure',
    getValue: (cluster) => cluster.hasFracturePressureResult ? nonNegative(cluster.fracturePressureMpa) : undefined,
  },
  net_pressure: {
    id: 'net_pressure', label: '净压力', unit: 'MPa', group: 'Pressure / Stress',
    capability: 'AVAILABLE', dataType: 'CONTINUOUS', granularity: 'SURFACE_VERTEX', colorMap: 'plasma',
    getValue: (cluster) => cluster.hasNetPressureResult ? nonNegative(cluster.netPressureMpa) : undefined,
  },
  local_shmin: {
    id: 'local_shmin', label: '局部最小水平主应力', unit: 'MPa', group: 'Pressure / Stress',
    capability: 'AVAILABLE', dataType: 'CONTINUOUS', granularity: 'SURFACE_VERTEX', colorMap: 'pressure',
    getValue: (cluster) => nonNegative(cluster.localShminMpa),
  },
  stress_shadow: {
    id: 'stress_shadow', label: '应力阴影增量', unit: 'MPa', group: 'Pressure / Stress',
    capability: 'AVAILABLE', capabilityReason: '当前求解器输出非负应力增量；色标固定以 0 为中心。',
    dataType: 'DIVERGING', granularity: 'FRAME_SCALAR', colorMap: 'diverging',
    getValue: (cluster) => finite(cluster.stressShadowMpa),
  },
  proppant_concentration: {
    id: 'proppant_concentration', label: '支撑剂浓度', unit: 'kg/m³', group: 'Proppant',
    capability: 'PARTIAL', capabilityReason: 'SURFACE_2D_V1 使用求解器逐 cell 浓度；旧模型仍使用簇/时间步代表值。',
    dataType: 'CONTINUOUS', granularity: 'SURFACE_CELL', colorMap: 'plasma',
    branchGranularity: 'BRANCH_SEGMENT',
    getValue: (cluster) => nonNegative(cluster.proppantConcentrationKgm3),
  },
  proppant_areal_concentration: {
    id: 'proppant_areal_concentration', label: '支撑剂面浓度', unit: 'kg/m²', group: 'Proppant',
    capability: 'PARTIAL', capabilityReason: 'SURFACE_2D_V1 输出真实逐 cell 铺置面密度；旧模型保持不可用。',
    dataType: 'CONTINUOUS', granularity: 'SURFACE_CELL', colorMap: 'plasma',
  },
  proppant_bed: {
    id: 'proppant_bed', label: 'Branch 支撑剂砂床', unit: 'mm', group: 'Proppant',
    capability: 'PARTIAL', capabilityReason: 'ENGINEERING_V1 输出有限天然裂缝 Branch 的逐段砂床厚度。',
    dataType: 'CONTINUOUS', granularity: 'BRANCH_SEGMENT', branchGranularity: 'BRANCH_SEGMENT',
    colorMap: 'plasma',
  },
  proppant_type: {
    id: 'proppant_type', label: '支撑剂类型', unit: '', group: 'Proppant',
    capability: 'AVAILABLE', dataType: 'CATEGORICAL', granularity: 'FRAME_SCALAR', colorMap: 'categorical',
    getCategory: (cluster) => cluster.proppantType,
  },
  conductivity: {
    id: 'conductivity', label: '裂缝导流能力（工程估算）', unit: 'D·mm', group: 'Post-Frac',
    capability: 'PARTIAL', capabilityReason: 'SURFACE_2D_V1 压后阶段输出逐 surface-cell 导流能力；旧模型仍为簇代表值。',
    dataType: 'CONTINUOUS', granularity: 'SURFACE_CELL', colorMap: 'inferno',
    branchGranularity: 'BRANCH_SEGMENT',
    getValue: (cluster) => nonNegative(cluster.conductivity),
  },
  fluid_type: {
    id: 'fluid_type', label: '压裂液类型', unit: '', group: 'Fluid',
    capability: 'AVAILABLE', dataType: 'CATEGORICAL', granularity: 'FRAME_SCALAR', colorMap: 'categorical',
    getCategory: (cluster) => cluster.fluidType,
  },
};

export const RESULT_GROUPS: ResultGroup[] = ['Geometry', 'Pressure / Stress', 'Proppant', 'Post-Frac', 'Fluid'];
const LEGACY_IDS: Record<string, ResultPropertyId> = {
  fracture_width: 'dynamic_width', DYNAMIC_WIDTH: 'dynamic_width',
  FRACTURE_PRESSURE: 'fracture_pressure', NET_PRESSURE: 'net_pressure', STRESS_SHADOW: 'stress_shadow',
};

export const normalizeResultPropertyId = (value: unknown): ResultPropertyId => {
  const key = String(value ?? '');
  if (key in RESULT_PROPERTIES) return key as ResultPropertyId;
  return LEGACY_IDS[key] ?? 'dynamic_width';
};

export const surfaceNodeResultValue = (
  node: { width?: number; pressure?: number; netPressure?: number; Shmin?: number; proppantConcentration?: number; proppantArealConcentration?: number; effectiveProppedWidth?: number; conductivity?: number },
  propertyId: ResultPropertyId,
): number | undefined => {
  if (propertyId === 'dynamic_width') return finite(node.width) === undefined ? undefined : Number(node.width) * 1000;
  if (propertyId === 'fracture_pressure') return finite(node.pressure) === undefined ? undefined : Number(node.pressure) / 1e6;
  if (propertyId === 'net_pressure') return finite(node.netPressure) === undefined ? undefined : Number(node.netPressure) / 1e6;
  if (propertyId === 'local_shmin') return finite(node.Shmin) === undefined ? undefined : Number(node.Shmin) / 1e6;
  if (propertyId === 'proppant_concentration') return finite(node.proppantConcentration);
  if (propertyId === 'proppant_areal_concentration') return finite(node.proppantArealConcentration);
  if (propertyId === 'propped_width') return finite(node.effectiveProppedWidth) === undefined ? undefined : Number(node.effectiveProppedWidth) * 1000;
  if (propertyId === 'conductivity') return finite(node.conductivity);
  return undefined;
};

export const branchResultValue = (
  branch: { proppant?: { concentrationKgM3?: number; bedThicknessM?: number; effectiveProppedApertureM?: number; conductivityDmm?: number } | null },
  propertyId: ResultPropertyId,
): number | undefined => {
  const state = branch.proppant;
  if (!state) return undefined;
  if (propertyId === 'proppant_concentration') return nonNegative(state.concentrationKgM3);
  if (propertyId === 'proppant_bed') {
    const value = nonNegative(state.bedThicknessM);
    return value === undefined ? undefined : value * 1000;
  }
  if (propertyId === 'propped_width') {
    const value = nonNegative(state.effectiveProppedApertureM);
    return value === undefined ? undefined : value * 1000;
  }
  if (propertyId === 'conductivity') return nonNegative(state.conductivityDmm);
  return undefined;
};

export const branchSegmentResultValue = (
  segment: { concentrationKgM3?: number; bedThicknessM?: number; effectiveProppedApertureM?: number; conductivityDmm?: number },
  propertyId: ResultPropertyId,
): number | undefined => {
  if (propertyId === 'proppant_concentration') return nonNegative(segment.concentrationKgM3);
  if (propertyId === 'proppant_bed') {
    const value = nonNegative(segment.bedThicknessM);
    return value === undefined ? undefined : value * 1000;
  }
  if (propertyId === 'propped_width') {
    const value = nonNegative(segment.effectiveProppedApertureM);
    return value === undefined ? undefined : value * 1000;
  }
  if (propertyId === 'conductivity') return nonNegative(segment.conductivityDmm);
  return undefined;
};

const valuesInFrames = (frames: SimulationPlaybackFrame[], definition: ResultPropertyDefinition): number[] => {
  const clusterValues = definition.getValue ? frames.flatMap((frame) => frame.clusters
    .filter((cluster) => cluster.isInitiated)
    .map(definition.getValue!)
    .filter((value): value is number => value !== undefined && Number.isFinite(value))) : [];
  const surfaceValues = frames.flatMap((frame) => frame.clusters.flatMap((cluster) => {
    const surfaceNodes = cluster.surfaceFrame?.model === 'DIRECTIONAL_ADAPTIVE_SURFACE_V1'
      ? Object.values(cluster.surfaceFrame.wings ?? {}).flatMap((wing) => wing.nodes ?? [])
      : cluster.surfaceFrame?.nodes ?? [];
    return surfaceNodes
      .map((node) => surfaceNodeResultValue(node, definition.id))
      .filter((value): value is number => value !== undefined && Number.isFinite(value));
  }));
  const branchValues = frames.flatMap((frame) => frame.clusters.flatMap((cluster) =>
    (cluster.surfaceFrame?.branches ?? [])
      .flatMap((branch) => branch.proppant?.segments?.length
        ? branch.proppant.segments.map((segment) => branchSegmentResultValue(segment, definition.id))
        : [branchResultValue(branch, definition.id)])
      .filter((value): value is number => value !== undefined && Number.isFinite(value))));
  return [...surfaceValues, ...branchValues].length
    ? [...surfaceValues, ...branchValues]
    : clusterValues;
};

const quantile = (sorted: number[], fraction: number): number => {
  if (sorted.length === 1) return sorted[0];
  const index = Math.max(0, Math.min(sorted.length - 1, (sorted.length - 1) * fraction));
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  return sorted[lower] + (sorted[upper] - sorted[lower]) * (index - lower);
};

const withDivergingCenter = (range: ResultRange, definition: ResultPropertyDefinition): ResultRange => {
  if (definition.dataType !== 'DIVERGING' || !range.available) return range;
  const extent = Math.max(Math.abs(range.min), Math.abs(range.max));
  return { ...range, min: -extent, max: extent, constant: extent <= 1e-12, center: 0 };
};

export const calculateResultRange = (
  playback: SimulationPlayback,
  propertyId: ResultPropertyId,
  options: {
    scope?: ResultRangeScope;
    currentFrame?: SimulationPlaybackFrame | null;
    method?: ResultRangeMethod;
    manualMin?: number;
    manualMax?: number;
  } = {},
): ResultRange => {
  const definition = RESULT_PROPERTIES[propertyId];
  if (definition.dataType === 'CATEGORICAL' || definition.capability === 'NOT_AVAILABLE') {
    return { min: 0, max: 0, available: false, constant: false };
  }
  const method = options.method ?? 'MIN_MAX';
  if (method === 'MANUAL') {
    const min = Number(options.manualMin);
    const max = Number(options.manualMax);
    if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) {
      return { min: 0, max: 0, available: false, constant: false };
    }
    return withDivergingCenter({ min, max, available: true, constant: false }, definition);
  }
  const frames = options.scope === 'CURRENT_FRAME' && options.currentFrame ? [options.currentFrame] : playback.frames;
  const values = valuesInFrames(frames, definition);
  if (!values.length) return { min: 0, max: 0, available: false, constant: false };
  const sorted = [...values].sort((a, b) => a - b);
  const min = method === 'P2_P98' ? quantile(sorted, 0.02) : sorted[0];
  const max = method === 'P2_P98' ? quantile(sorted, 0.98) : sorted[sorted.length - 1];
  return withDivergingCenter({
    min, max, available: true, constant: Math.abs(max - min) <= 1e-12, clipped: method === 'P2_P98',
  }, definition);
};

export const normalizeResultValue = (value: number, range: ResultRange): number | undefined => {
  if (!range.available || !Number.isFinite(value)) return undefined;
  if (range.constant || Math.abs(range.max - range.min) <= 1e-12) return 0.5;
  return Math.min(1, Math.max(0, (value - range.min) / (range.max - range.min)));
};

const channel = (hex: string, offset: number) => Number.parseInt(hex.slice(offset, offset + 2), 16);
const interpolateColor = (normalized: number, palette: readonly string[]): string => {
  const scaled = Math.min(1, Math.max(0, normalized)) * (palette.length - 1);
  const lower = Math.min(palette.length - 2, Math.floor(scaled));
  const fraction = scaled - lower;
  const from = palette[lower], to = palette[lower + 1];
  const rgb = [1, 3, 5].map((offset) =>
    Math.round(channel(from, offset) + (channel(to, offset) - channel(from, offset)) * fraction));
  return `#${rgb.map((part) => part.toString(16).padStart(2, '0')).join('')}`;
};

export const resultColor = (value: number, range: ResultRange, propertyId: ResultPropertyId): string => {
  const normalized = normalizeResultValue(value, range);
  if (normalized === undefined) return '#8A94A3';
  return interpolateColor(normalized, COLOR_MAP_REGISTRY[RESULT_PROPERTIES[propertyId].colorMap]);
};

export const categoryColor = (category: string): string => {
  let hash = 0;
  for (let index = 0; index < category.length; index += 1) hash = ((hash << 5) - hash + category.charCodeAt(index)) | 0;
  const palette = COLOR_MAP_REGISTRY.categorical;
  return palette[Math.abs(hash) % palette.length];
};

export const resultCategories = (frame: SimulationPlaybackFrame | null, propertyId: ResultPropertyId): string[] => {
  const getCategory = RESULT_PROPERTIES[propertyId].getCategory;
  if (!frame || !getCategory) return [];
  return [...new Set(frame.clusters.filter((cluster) => cluster.isInitiated)
    .map(getCategory).filter((value): value is string => Boolean(value)))].sort();
};

export const resultTicks = (range: ResultRange, count = 5): number[] => {
  if (!range.available) return [];
  if (range.constant) return [range.min];
  return Array.from({ length: count }, (_, index) =>
    range.max - ((range.max - range.min) * index) / (count - 1));
};

export const resultGradient = (propertyId: ResultPropertyId): string =>
  `linear-gradient(to bottom, ${[...COLOR_MAP_REGISTRY[RESULT_PROPERTIES[propertyId].colorMap]].reverse().join(', ')})`;
